/* ==========================================================================
   Garnkit — cart behaviour for grouped kit cards
   theme/assets/kit-cart.js

   WHAT IT ADDS
   The theme has no notion of a kit, so it has no hook that removes several
   cart lines at once. This file adds exactly that hook, plus the two smaller
   pieces the grouped card needs:

     1. Remove a whole kit in ONE /cart/update.js call — every line of the
        group is set to 0 in the same request, so the cart is never briefly
        half a kit and the customer never sees an intermediate total.
     2. Refresh through the Section Rendering API when the cart lives in a
        section, falling back to a full page reload when it does not.
     3. Suppress the per-line quantity stepper on kit lines. A kit is added as
        one kit (v1), and letting a customer change one yarn line's quantity
        would silently break the recipe.

   WORKS IN BOTH SURFACES
   The click handler is delegated from `document`, so it survives every
   re-render — drawer, cart page, section swap, or a theme that rebuilds the
   drawer from scratch. Nothing here is bound to a specific container.

   DEFENSIVE BY DESIGN
   Simple Bundles v2 is installed on this shop and may rewrite cart lines, so
   nothing is trusted:
     · the group is recomputed from a fresh /cart.js before removing, so lines
       added to the group after render are included and lines already gone are
       not resent;
     · line item KEYS are used, never positional line numbers, so another
       app reordering the cart cannot make us zero the wrong line;
     · if the group has already vanished, the click resolves to a refresh
       instead of an error;
     · every network step has a failure path that ends in a consistent UI.

   NO NEW CUSTOMER COPY
   Danish copy is fixed by the spec and rendered in Liquid. The only string
   this file can display is the theme's own translated cart error, when the
   theme exposes one. It never invents customer-facing text.

   Plain ES5, no dependencies, no Promise, no fetch — the same transport the
   theme itself is safe with.
   ========================================================================== */

(function () {
  'use strict';

  var GROUP_SELECTOR = '[data-kit-group]';
  var REMOVE_SELECTOR = '[data-kit-remove]';
  var CARD_SCOPE = '.kit-cart';

  /* Line item keys are `<variant id>:<hex>`. Anything outside this alphabet is
     rejected before it is ever put into a selector or a request body. */
  var KEY_PATTERN = /^[A-Za-z0-9:_-]+$/;

  /* Attributes themes use to stamp a line item key onto a cart row. */
  var KEY_ATTRIBUTES = [
    'data-key',
    'data-id',
    'data-line-item-key',
    'data-cart-item-key',
    'data-item-key',
    'data-variant-key'
  ];

  /* Row containers, widest-first fallback list. */
  var ROW_SELECTOR = 'tr, li, .cart__row, .cart__item, .cart-item, .ajaxcart__row, [data-cart-item]';

  /* Quantity widgets. Only ever queried INSIDE a known kit row. */
  var QTY_WRAPPERS = '[data-quantity-selector], [data-quantity-wrapper], .js-qty, .quantity-selector, .ajaxcart__qty, .cart__qty, .quantity';
  var QTY_INPUTS = 'input[name="updates[]"], input[name^="updates["], input[data-quantity-input], input.js-qty__num, input[type="number"]';

  /* Elements whose enclosing section should be re-rendered after a change. */
  var REFRESH_HINTS = [
    '[data-cart-count]',
    '[data-cart-item-count]',
    '[data-cart-subtotal]',
    '[data-cart-drawer]',
    '[data-section-type="cart-template"]',
    '[data-section-type="cart-drawer"]',
    '#CartDrawer',
    '#CartCount',
    '.cart-link__bubble',
    '.site-header__cart-count',
    '.cart__subtotal',
    'form[action$="/cart"]'
  ].join(',');

  var MAX_SECTIONS = 6;

  /* Keys of lines known to belong to a kit, learned from rendered cards and
     from every cart payload this file sees. */
  var knownKitKeys = {};

  /* ------------------------------------------------------------------ *
   * Small DOM helpers (ES5, no polyfills assumed)
   * ------------------------------------------------------------------ */

  function each(list, fn) {
    if (!list) return;
    for (var i = 0; i < list.length; i++) fn(list[i], i);
  }

  function matches(el, selector) {
    if (!el || el.nodeType !== 1) return false;
    var fn = el.matches || el.msMatchesSelector || el.webkitMatchesSelector;
    return fn ? fn.call(el, selector) : false;
  }

  function closest(el, selector) {
    var node = el;
    while (node && node.nodeType === 1) {
      if (matches(node, selector)) return node;
      node = node.parentElement || node.parentNode;
    }
    return null;
  }

  function attr(el, name) {
    return (el && el.getAttribute && el.getAttribute(name)) || '';
  }

  function splitKeys(value) {
    var out = [];
    if (!value) return out;
    var parts = String(value).split(',');
    for (var i = 0; i < parts.length; i++) {
      var key = parts[i].replace(/^\s+|\s+$/g, '');
      if (key && KEY_PATTERN.test(key) && out.indexOf(key) === -1) out.push(key);
    }
    return out;
  }

  function size(object) {
    var n = 0;
    for (var k in object) {
      if (Object.prototype.hasOwnProperty.call(object, k)) n++;
    }
    return n;
  }

  /* ------------------------------------------------------------------ *
   * Routes — locale-aware, never hardcoded
   * ------------------------------------------------------------------ */

  function cartRoot() {
    if (window.Shopify && window.Shopify.routes && typeof window.Shopify.routes.root === 'string') {
      return window.Shopify.routes.root;
    }
    var add = window.theme && window.theme.routes && window.theme.routes.cartAdd;
    if (typeof add === 'string' && add.indexOf('cart/add') !== -1) {
      return add.split('cart/add')[0];
    }
    return '/';
  }

  function route(file) {
    var root = cartRoot();
    if (root.charAt(root.length - 1) !== '/') root += '/';
    return root + file;
  }

  /* ------------------------------------------------------------------ *
   * Transport — XHR + JSON, callback style
   * ------------------------------------------------------------------ */

  function request(method, url, body, onOk, onError) {
    var xhr;
    try {
      xhr = new XMLHttpRequest();
    } catch (e) {
      onError(0, null);
      return;
    }
    xhr.open(method, url, true);
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
    if (body) xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.onload = function () {
      var parsed = null;
      try {
        parsed = JSON.parse(xhr.responseText);
      } catch (e) {
        parsed = null;
      }
      if (xhr.status >= 200 && xhr.status < 300 && parsed) onOk(parsed);
      else onError(xhr.status, parsed);
    };
    xhr.onerror = function () {
      onError(0, null);
    };
    xhr.send(body ? JSON.stringify(body) : null);
  }

  /* ------------------------------------------------------------------ *
   * Section Rendering API
   * ------------------------------------------------------------------ */

  function sectionIdOf(el) {
    var host = closest(el, '[id^="shopify-section-"]');
    if (host && host.id) return host.id.replace('shopify-section-', '');
    var owner = closest(el, '[data-section-id]');
    if (owner) return attr(owner, 'data-section-id');
    return '';
  }

  function sectionsToRefresh() {
    var ids = [];
    var collect = function (el) {
      if (ids.length >= MAX_SECTIONS) return;
      var id = sectionIdOf(el);
      if (id && ids.indexOf(id) === -1) ids.push(id);
    };
    each(document.querySelectorAll(GROUP_SELECTOR), collect);
    each(document.querySelectorAll(REFRESH_HINTS), collect);
    return ids;
  }

  function applySections(sections) {
    if (!sections || typeof sections !== 'object') return 0;
    if (typeof window.DOMParser !== 'function' && typeof window.DOMParser !== 'object') return 0;

    var applied = 0;
    for (var id in sections) {
      if (!Object.prototype.hasOwnProperty.call(sections, id)) continue;
      var html = sections[id];
      if (typeof html !== 'string' || !html) continue;

      var target = document.getElementById('shopify-section-' + id) ||
                   document.querySelector('[data-section-id="' + id + '"]');
      if (!target) continue;

      var source = null;
      try {
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        source = parsed.getElementById('shopify-section-' + id) ||
                 parsed.querySelector('[data-section-id="' + id + '"]');
      } catch (e) {
        source = null;
      }
      if (!source) continue;

      target.innerHTML = source.innerHTML;
      applied++;

      /* Archetype registers its sections through `shopify:section:load`, so
         re-dispatching it re-binds the drawer, the quantity widgets and the
         theme's own cart scripts inside the markup we just swapped in. */
      try {
        target.dispatchEvent(new CustomEvent('shopify:section:load', {
          bubbles: true,
          detail: { sectionId: id }
        }));
      } catch (e) {
        /* CustomEvent unavailable — the theme simply re-binds on next load. */
      }
    }
    return applied;
  }

  function announce(cart) {
    try {
      document.dispatchEvent(new CustomEvent('kit-cart:updated', {
        bubbles: true,
        detail: { cart: cart }
      }));
    } catch (e) {
      /* Informational only. */
    }
  }

  function hardRefresh() {
    window.location.reload();
  }

  /* ------------------------------------------------------------------ *
   * Kit key bookkeeping
   * ------------------------------------------------------------------ */

  function learnKeysFromDom() {
    each(document.querySelectorAll(GROUP_SELECTOR), function (card) {
      var keys = splitKeys(attr(card, 'data-kit-keys'));
      for (var i = 0; i < keys.length; i++) knownKitKeys[keys[i]] = true;
    });
  }

  function learnKeysFromCart(cart) {
    if (!cart || !cart.items || !cart.items.length) return;
    for (var i = 0; i < cart.items.length; i++) {
      var line = cart.items[i];
      var props = line && line.properties;
      if (props && props._kit_id && line.key && KEY_PATTERN.test(line.key)) {
        knownKitKeys[line.key] = true;
      }
    }
  }

  /* ------------------------------------------------------------------ *
   * Quantity stepper suppression on kit lines
   * ------------------------------------------------------------------ */

  function lockQuantity(row) {
    /* Inputs are made read-only rather than disabled on purpose: a disabled
       input is not submitted, and themes that post a positional `updates[]`
       array would then shift every following line's quantity. Hidden inputs
       still submit, so the cart form stays correct. */
    each(row.querySelectorAll(QTY_INPUTS), function (input) {
      input.readOnly = true;
      input.tabIndex = -1;
      input.setAttribute('aria-readonly', 'true');
      var wrapper = closest(input, QTY_WRAPPERS);
      if (wrapper && wrapper !== row) wrapper.style.display = 'none';
      else input.style.display = 'none';
    });

    each(row.querySelectorAll(QTY_WRAPPERS), function (wrapper) {
      if (wrapper === row) return;
      wrapper.style.display = 'none';
    });
  }

  function syncQuantityLocks() {
    learnKeysFromDom();

    for (var key in knownKitKeys) {
      if (!Object.prototype.hasOwnProperty.call(knownKitKeys, key)) continue;
      if (!KEY_PATTERN.test(key)) continue;

      var selector = [];
      for (var i = 0; i < KEY_ATTRIBUTES.length; i++) {
        selector.push('[' + KEY_ATTRIBUTES[i] + '="' + key + '"]');
      }

      var nodes;
      try {
        nodes = document.querySelectorAll(selector.join(','));
      } catch (e) {
        continue;
      }

      each(nodes, function (node) {
        /* Our own card renders no stepper; never touch it. */
        if (closest(node, CARD_SCOPE)) return;
        var row = closest(node, ROW_SELECTOR) || node;
        if (attr(row, 'data-kit-qty-locked') === 'true') return;
        row.setAttribute('data-kit-qty-locked', 'true');
        lockQuantity(row);
      });
    }

    /* Fallback: scan every direct child of [data-products] — the theme's drawer
       JS may render items with no standard key attributes. Any row where a
       descendant carries an attribute whose value equals a known kit key, or
       whose href contains the key, is treated as a kit line and hidden. */
    each(document.querySelectorAll('[data-products] > *'), function (row) {
      if (closest(row, CARD_SCOPE)) return;
      if (attr(row, 'data-kit-qty-locked') === 'true') return;

      var descendants = row.querySelectorAll('*');
      var all = [row];
      for (var d = 0; d < descendants.length; d++) all.push(descendants[d]);

      for (var e = 0; e < all.length; e++) {
        var el = all[e];
        /* Check every attribute value for an exact key match. */
        var attrList = el.attributes;
        for (var a = 0; a < attrList.length; a++) {
          if (knownKitKeys[attrList[a].value]) {
            row.setAttribute('data-kit-qty-locked', 'true');
            return;
          }
        }
        /* Check href for partial key match (remove / change links). */
        var href = attr(el, 'href');
        if (href) {
          for (var key2 in knownKitKeys) {
            if (Object.prototype.hasOwnProperty.call(knownKitKeys, key2) &&
                href.indexOf(key2) !== -1) {
              row.setAttribute('data-kit-qty-locked', 'true');
              return;
            }
          }
        }
      }
    });
  }

  /* ------------------------------------------------------------------ *
   * Button state
   * ------------------------------------------------------------------ */

  function lock(card, button) {
    if (card) card.setAttribute('data-kit-busy', 'true');
    if (button) {
      button.setAttribute('aria-disabled', 'true');
      button.disabled = true;
    }
  }

  function unlock(card, button) {
    if (card) card.removeAttribute('data-kit-busy');
    if (button) {
      button.removeAttribute('aria-disabled');
      button.disabled = false;
    }
  }

  /* The only string this file can render, and only when the theme provides a
     translated one. No Danish copy is invented here. */
  function showThemeError(card) {
    var strings = (window.theme && window.theme.strings) || {};
    var message = strings.cartError || strings.cartQuantityError || '';
    if (!card || !message) return;
    var node = card.querySelector('.kit-cart__error');
    if (!node) {
      node = document.createElement('p');
      node.className = 'kit-cart__error';
      node.setAttribute('role', 'status');
      /* The cart-page card is a grid; put the message inside the identity
         column so it cannot land in the quantity or amount cell. */
      (card.querySelector('.kit-cart__ident') || card).appendChild(node);
    }
    node.textContent = message;
  }

  function clearThemeError(card) {
    if (!card) return;
    var node = card.querySelector('.kit-cart__error');
    if (node && node.parentNode) node.parentNode.removeChild(node);
  }

  /* ------------------------------------------------------------------ *
   * Removing a whole kit in one call
   * ------------------------------------------------------------------ */

  function buildUpdates(cart, kitId, fallbackKeys) {
    var updates = {};
    var i;

    if (cart && cart.items && cart.items.length) {
      for (i = 0; i < cart.items.length; i++) {
        var line = cart.items[i];
        if (!line || !line.key || !KEY_PATTERN.test(line.key)) continue;
        var props = line.properties || {};
        var belongs = (kitId && props._kit_id === kitId) ||
                      (fallbackKeys.indexOf(line.key) !== -1);
        if (belongs) updates[line.key] = 0;
      }
      return updates;
    }

    /* No cart payload to reconcile against — trust what was rendered. */
    for (i = 0; i < fallbackKeys.length; i++) updates[fallbackKeys[i]] = 0;
    return updates;
  }

  /* Fire the theme's standard cart events so it can refresh [data-products],
     the cart count badge, and the empty-cart state — without a page reload. */
  function fireCartEvents(cart) {
    var events = ['cart:updated', 'cart:refresh', 'cart:build'];
    for (var i = 0; i < events.length; i++) {
      try {
        document.dispatchEvent(new CustomEvent(events[i], {
          bubbles: true,
          detail: { cart: cart }
        }));
      } catch (e) {
        /* Informational only. */
      }
    }
  }

  function sendUpdates(updates, card, button) {
    var body = { updates: updates };
    var ids = sectionsToRefresh();
    if (ids.length) {
      body.sections = ids.join(',');
      body.sections_url = window.location.pathname + window.location.search;
    }

    request('POST', route('cart/update.js'), body, function (cart) {
      learnKeysFromCart(cart);
      announce(cart);

      /* Update the kit groups container directly from the returned section HTML.
         Targeting only #KitCartGroupsDrawer avoids replacing the entire header
         section, which would reset the drawer to its closed state. */
      if (cart.sections && typeof window.DOMParser !== 'undefined') {
        var liveWrapper = document.getElementById('KitCartGroupsDrawer');
        if (liveWrapper) {
          for (var sId in cart.sections) {
            if (!Object.prototype.hasOwnProperty.call(cart.sections, sId)) continue;
            var sHtml = cart.sections[sId];
            if (!sHtml || typeof sHtml !== 'string') continue;
            var freshWrapper = null;
            try {
              freshWrapper = new DOMParser()
                .parseFromString(sHtml, 'text/html')
                .getElementById('KitCartGroupsDrawer');
            } catch (e) {}
            if (freshWrapper) {
              liveWrapper.innerHTML = freshWrapper.innerHTML;
              break;
            }
          }
        }
      }

      /* Let the theme refresh [data-products] and the cart count. Our
         MutationObserver will hide any kit lines that reappear. */
      fireCartEvents(cart);
      syncQuantityLocks();
    }, function () {
      /* /cart/update.js is atomic: a failed call changed nothing, so the card
         is still valid. Restore it and let the customer try again. */
      unlock(card, button);
      showThemeError(card);
    });
  }

  function removeGroup(button) {
    var card = closest(button, GROUP_SELECTOR);
    if (card && attr(card, 'data-kit-busy') === 'true') return;

    var kitId = attr(card, 'data-kit-id') || attr(button, 'data-kit-id');
    var keys = splitKeys(attr(card, 'data-kit-keys') || attr(button, 'data-kit-keys'));

    if (!kitId && !keys.length) {
      /* Nothing identifies this group any more. A reload shows the truth
         rather than sending a request that could zero the wrong line. */
      hardRefresh();
      return;
    }

    clearThemeError(card);
    lock(card, button);

    request('GET', route('cart.js'), null, function (cart) {
      learnKeysFromCart(cart);
      var updates = buildUpdates(cart, kitId, keys);
      if (!size(updates)) {
        /* The group is already gone — another app removed or rewrote it.
           Nothing to send; just show the cart as it really is. */
        hardRefresh();
        return;
      }
      sendUpdates(updates, card, button);
    }, function () {
      /* Could not read the cart. Fall back to the keys we rendered; the
         server ignores keys that no longer exist. */
      var updates = buildUpdates(null, kitId, keys);
      if (!size(updates)) {
        hardRefresh();
        return;
      }
      sendUpdates(updates, card, button);
    });
  }

  /* Public entry point, so the configurator or a theme script can remove a
     kit without a click (e.g. after an "edit kit" flow re-adds it). */
  function removeKitById(kitId) {
    if (!kitId) return;
    var cards = document.querySelectorAll(GROUP_SELECTOR);
    for (var i = 0; i < cards.length; i++) {
      if (attr(cards[i], 'data-kit-id') === kitId) {
        var button = cards[i].querySelector(REMOVE_SELECTOR);
        removeGroup(button || cards[i]);
        return;
      }
    }
    /* Not on screen: act on the id alone. */
    request('GET', route('cart.js'), null, function (cart) {
      var updates = buildUpdates(cart, kitId, []);
      if (size(updates)) sendUpdates(updates, null, null);
    }, function () {});
  }

  /* ------------------------------------------------------------------ *
   * Wiring
   * ------------------------------------------------------------------ */

  function onClick(event) {
    if (event.defaultPrevented) return;
    if (event.button !== undefined && event.button !== 0) return;
    var button = closest(event.target, REMOVE_SELECTOR);
    if (!button) return;
    event.preventDefault();
    removeGroup(button);
  }

  var syncQueued = false;

  function queueSync() {
    if (syncQueued) return;
    syncQueued = true;
    window.setTimeout(function () {
      syncQueued = false;
      syncQuantityLocks();
    }, 150);
  }

  function observe() {
    if (typeof window.MutationObserver !== 'function') return;
    var observer = new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        if (records[i].addedNodes && records[i].addedNodes.length) {
          queueSync();
          return;
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  function init() {
    document.addEventListener('click', onClick, false);

    /* Themes re-render the cart on their own schedule too. */
    document.addEventListener('cart:updated', queueSync, false);
    document.addEventListener('cart:refresh', queueSync, false);
    document.addEventListener('cart:build', queueSync, false);
    document.addEventListener('shopify:section:load', queueSync, false);

    syncQuantityLocks();
    observe();

    window.KitCart = {
      removeKitById: removeKitById,
      syncQuantityLocks: syncQuantityLocks,
      sectionsToRefresh: sectionsToRefresh,
      applySections: applySections
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, false);
  } else {
    init();
  }
})();
